import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const authVersion = useRef(0);

  const didRun = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const requestVersion = authVersion.current;

    const checkSession = async () => {
      try {
        const res = await fetch("/api/auth/userinfo", {
          method: "GET",
          credentials: "include",
        });

        // Ignore a response if login/logout happened meanwhile.
        if (
          cancelled ||
          requestVersion !== authVersion.current
        ) {
          return;
        }

        if (res.status === 401) {
          setUser(null);
          setIsAuthenticated(false);
          setIsAdmin(false);
          return;
        }

        if (!res.ok) {
          console.error("Session check failed:", res.status);
          return;
        }

        const data = await res.json();

        // Check again after parsing the response.
        if (
          cancelled ||
          requestVersion !== authVersion.current
        ) {
          return;
        }

        setUser(data);
        setIsAuthenticated(true);
        setIsAdmin(data.role === "admin");
      } catch (error) {
        if (
          !cancelled &&
          requestVersion === authVersion.current
        ) {
          console.error("Unable to check session:", error);
        }
      } finally {
        if (
          !cancelled &&
          requestVersion === authVersion.current
        ) {
          setLoading(false);
        }
      }
    };

    checkSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // ============================================================
  // LOGIN
  // ============================================================

  const login = (userData) => {
    authVersion.current += 1;

    setUser(userData);
    setIsAuthenticated(true);
    setIsAdmin(userData.role === "admin");
    setLoading(false);
  };

  // ============================================================
  // LOGOUT
  // ============================================================

  const logout = async () => {
    // Invalidate outstanding authentication requests immediately.
    authVersion.current += 1;

    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setLoading(false);

    try {
      const res = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        console.error("Logout failed:", res.status);
      }
    } catch (error) {
      console.error("Logout request failed:", error);
    } finally {
      navigate("/");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isAuthenticated,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {

  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return context;
};